import { useEffect, useMemo, useRef, useState } from 'react';
import type { InkDocumentV1, InkPoint } from '../../types/ink';

function absolutePoint(point: InkPoint, document: InkDocumentV1) {
  return {
    x: point.x <= 1 ? point.x * document.width : point.x,
    y: point.y <= 1 ? point.y * document.height : point.y,
    t: Math.max(0, point.t || 0),
  };
}

function durationFor(document: InkDocumentV1) {
  let max = 0;
  for (const stroke of document.strokes) for (const point of stroke.points) max = Math.max(max, Number(point.t) || 0);
  return Math.max(1, max);
}

function formatReplayTime(ms: number) {
  const seconds = Math.max(0, ms) / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)} dtk`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
}

export function InkReplayViewer({ document }: { document: InkDocumentV1 }) {
  const duration = useMemo(() => durationFor(document), [document]);
  const pointerTypes = useMemo(() => [...new Set(document.strokes.map((stroke) => stroke.pointerType))], [document]);
  const [position, setPosition] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const raf = useRef<number | null>(null);
  const startedAt = useRef(0);
  const startedFrom = useRef(0);

  useEffect(() => {
    setPosition(0);
    setPlaying(false);
  }, [document]);

  useEffect(() => () => { if (raf.current !== null) cancelAnimationFrame(raf.current); }, []);

  useEffect(() => {
    if (!playing) return;
    startedAt.current = performance.now();
    startedFrom.current = position;
    const tick = (now: number) => {
      const next = Math.min(duration, startedFrom.current + ((now - startedAt.current) * speed));
      setPosition(next);
      if (next >= duration) {
        setPlaying(false);
        raf.current = null;
        return;
      }
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => { if (raf.current !== null) cancelAnimationFrame(raf.current); raf.current = null; };
  }, [playing, duration, speed]);

  const visiblePaths = document.strokes.map((stroke) => {
    const points = stroke.points.map((point) => absolutePoint(point, document)).filter((point) => point.t <= position);
    return {
      id: stroke.id,
      d: points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' '),
    };
  }).filter((path) => path.d);

  function toggle() {
    if (position >= duration) setPosition(0);
    setPlaying((current) => !current);
  }

  return (
    <div className="ink-replay-viewer">
      <div className="ink-replay-viewer__meta" aria-label="Informasi rekaman tulisan">
        <span><b>{document.strokes.length}</b> goresan</span>
        <span><b>{formatReplayTime(duration)}</b> durasi</span>
        <span><b>{pointerTypes.join(', ') || 'unknown'}</b> input</span>
      </div>
      <svg viewBox={`0 0 ${document.width} ${document.height}`} role="img" aria-label="Putar ulang urutan tulisan">
        <rect x="0" y="0" width={document.width} height={document.height} className="ink-replay-viewer__paper" />
        {visiblePaths.map((path) => <path key={path.id} d={path.d} className="ink-replay-viewer__stroke" vectorEffect="non-scaling-stroke" />)}
      </svg>
      <div className="ink-replay-viewer__controls" aria-label="Kontrol putar ulang tulisan">
        <button className="btn btn--primary" type="button" onClick={toggle} aria-label={playing ? 'Jeda rekaman tulisan' : 'Putar rekaman tulisan'}>{playing ? 'Jeda' : position > 0 && position < duration ? 'Lanjutkan' : 'Putar Ulang'}</button>
        <button className="btn" type="button" onClick={() => { setPlaying(false); setPosition(0); }} aria-label="Kembali ke awal rekaman">Dari Awal</button>
        <input aria-label="Posisi rekaman tulisan" type="range" min="0" max={duration} value={position} onChange={(event) => { setPlaying(false); setPosition(Number(event.target.value)); }} />
        <span className="ink-replay-viewer__time">{formatReplayTime(position)} / {formatReplayTime(duration)}</span>
        <label className="ink-replay-viewer__speed">Kecepatan <select aria-label="Kecepatan putar ulang" value={speed} onChange={(event) => setSpeed(Number(event.target.value))}><option value={0.5}>0.5x</option><option value={1}>1x</option><option value={2}>2x</option></select></label>
      </div>
    </div>
  );
}
